<template>
  <header
    class="relative flex h-screen overflow-hidden text-white lg:-mt-nav-lg"
  >
    <template v-if="data.video">
      <video
        autoplay
        muted
        loop
        playsinline
        class="absolute size-full object-cover object-right-top"
        :poster="data.image?.permalink ?? undefined"
        :class="{
          'max-lg:hidden': data.video_mobile,
        }"
      >
        <source :src="data.video.permalink" type="video/mp4" />
      </video>
      <video
        v-if="data.video_mobile"
        autoplay
        muted
        loop
        playsinline
        class="absolute size-full object-cover object-right-top lg:hidden"
        :poster="
          data.mobile_image?.permalink ?? data.image?.permalink ?? undefined
        "
      >
        <source :src="data.video_mobile.permalink" type="video/mp4" />
      </video>
    </template>
    <template v-else>
      <AppImage
        :asset="data.image"
        class="absolute size-full object-cover object-right-top"
        sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw 3xl:100vw"
        :class="{
          'max-lg:hidden': data.mobile_image,
        }"
      />
      <AppImage
        v-if="data.mobile_image"
        :asset="data.mobile_image"
        class="absolute size-full object-cover object-right-top lg:hidden"
      />
    </template>

    <Container
      indent="tight"
      class="relative z-10 flex flex-1 flex-col pt-40 md:justify-center md:pt-0"
    >
      <div
        class="absolute"
        :class="{
          'top-40': data.text_position_height?.value === 'top',
          'bottom-20': data.text_position_height?.value === 'bottom',
          'top-1/2 -translate-y-1/2':
            data.text_position_height?.value === 'center',
          'left-8 lg:left-16': data.text_position_width?.value === 'left',
          'sm:right-8 lg:right-16': data.text_position_width?.value === 'right',
          'lg:left-1/2 lg:-translate-x-1/2':
            data.text_position_width?.value === 'center',
        }"
      >
        <h1
          v-if="data.heading"
          class="max-w-xl hyphens-auto break-words font-semibold"
          :class="{
            'text-gray-500': data.text_color?.value === 'black',
            'text-gray-10': data.text_color?.value === 'white',
            'text-gray-300': data.text_color?.value === 'gray',
            'text-3xl lg:text-5xl': data.text_size?.value === 'H1',
            'text-xl lg:text-3xl': data.text_size?.value === 'H2',
            'text-lg lg:text-2xl': data.text_size?.value === 'H3',
          }"
          :style="{
            filter: data.shadow_enabled
              ? 'drop-shadow(0 0 6px rgba(0, 0, 0, 0.3))'
              : 'none',
          }"
          v-html="data.heading"
        />
        <div
          v-if="data.text"
          class="ml-1 mt-2 max-w-lg text-sm font-medium uppercase tracking-[0.07rem] lg:mt-1"
          :class="{
            'text-black': data.text_color?.value === 'black',
            'text-white': data.text_color?.value === 'white',
            'text-gray-400': data.text_color?.value === 'gray',
          }"
          :style="{
            filter: data.shadow_enabled
              ? 'drop-shadow(0 0 6px rgba(0, 0, 0, 0.3))'
              : 'none',
          }"
          v-html="data.text"
        />
        <AppButton
          v-if="data.call_to_action_button_text && data.call_to_action_button"
          :to="cmsLink(data.call_to_action_button)"
          class="mt-5"
          variant="primary"
          >{{ data.call_to_action_button_text }}</AppButton
        >
      </div>
    </Container>

    <div
      v-if="data.link_text"
      class="absolute inset-x-0 bottom-0 z-10 flex justify-center"
    >
      <ScrollDown :text="data.link_text" />
    </div>
  </header>
</template>

<script setup lang="ts">
import type { AssetFragment } from '#gql'

defineProps<{
  data: {
    heading: string
    text: string
    link_text: string
    call_to_action_button_text?: string
    call_to_action_button?: string
    image: AssetFragment
    text_color: {
      value: 'white' | 'black' | 'gray'
    }
    text_size: {
      value: 'H1' | 'H2' | 'H3'
    }
    text_position_width: {
      value: 'left' | 'center' | 'right'
    }
    text_position_height: {
      value: 'top' | 'center' | 'bottom'
    }
    shadow_enabled: boolean
    mobile_image?: AssetFragment
    video: {
      permalink: string
    }
    video_mobile?: {
      permalink: string
    }
  }
}>()

const cmsLink = useCmsLink()
</script>
